
import axios from 'axios';
import { AccountingDocument } from '@gid/models';
import { Vue, Component, Prop, Watch } from 'vue-property-decorator';
import { getModule } from 'vuex-module-decorators';
import {
  CreateQueryParams,
  SConditionAND,
  SFields,
} from '@dataui/crud-request';
import DatePicker from '@gid/vue-common/components/DatePicker.vue';
import CrudListPagination from '@gid/vue-common/components/crud-list/CrudListPagination.vue';
import CrudListTable from '@gid/vue-common/components/crud-list/CrudListTable.vue';
import CrudListInfiniteScroll from '@gid/vue-common/components/crud-list/CrudListInfiniteScroll.vue';
import CrudListErrorMessage from '@gid/vue-common/components/crud-list/CrudListErrorMessage.vue';

import {
  AccountingDocsModule,
  AccountingDocsActionsEnum,
} from '../store/credit-notes.module';

enum AccountingDocsStatusEnum {
  PAID = 'paid',
  SENT = 'sent',
  RECEIVED = 'received',
}

@Component({
  components: {
    DatePicker,
    CrudListPagination,
    CrudListTable,
    CrudListInfiniteScroll,
    CrudListErrorMessage,
  },
})
export default class InvoicesList extends Vue {
  documentStatuses = AccountingDocsStatusEnum;
  downloadStarted = false;
  downloadDisabled = false;

  created() {
    this.fetchAccountingDocs();
  }

  downloadReport() {
    axios
      .get(`/api/partner/invoices/export`, {
        params: {
          from_date: this.store.filters.dateFrom,
          to_date: this.store.filters.dateTo,
          status: this.store.filters.status || undefined,
        },
      })
      .then((response) => {
        this.downloadStarted = true;
      });
  }

  get store(): AccountingDocsModule {
    return getModule(AccountingDocsModule, this.$store);
  }

  // Chat context helper methods for TypeScript class component
  setChatContext(key: string, value: any) {
    this.$store.dispatch('chatContext/setContext', { key, value });
  }

  clearChatContext(key?: string) {
    this.$store.dispatch('chatContext/clearContext', key);
  }

  get filterStatusOptions() {
    return Object.values(AccountingDocsStatusEnum);
  }

  get columns() {
    return [
      {
        label: this.$t('invoices.table.heading.issuing-date'),
        key: 'issueDate',
        sortable: true,
      },
      { label: this.$t('invoices.table.heading.status'), key: 'status' },
      {
        label: this.$t('invoices.table.heading.job-names'),
        key: 'jobNames',
      },
      {
        label: this.$t('invoices.table.heading.grand-total'),
        key: 'totalAmount',
        sortable: true,
      },
      {
        label: this.$t('invoices.table.heading.download'),
        key: 'download',
      },
    ];
  }

  @Watch('store.filters', {
    deep: true,
  })
  searchChange(value) {
    this.fetchAccountingDocs();
  }

  @Watch('store.items')
  itemsChange(newItems) {
    // Update chat context with simplified invoice data
    const simplifiedInvoices = this.simplifyInvoicesForContext(newItems);
    this.setChatContext('currentInvoices', simplifiedInvoices);
  }

  // Helper to create a simplified version of invoice data for chat context
  simplifyInvoicesForContext(invoices) {
    // store.items is an object, not an array, so convert it
    if (!invoices) {
      return [];
    }
    const invoicesArray = Array.isArray(invoices)
      ? invoices
      : Object.values(invoices);
    if (invoicesArray.length === 0) {
      return [];
    }
    return invoicesArray.map((invoice) => ({
      number: invoice.number,
      issueDate: invoice.issueDate,
      totalAmount: invoice.totalAmount,
      vatAmount: invoice.vatAmount,
      status: invoice.status,
      dueDate: invoice.dueDate,
      paid: invoice.paid,
      partnerName: invoice.partner_name,
      jobs: invoice.jobs?.map((job) => ({
        id: job.id,
        name: job.name,
      })),
      cancellationNumber: invoice.cancellationNumber,
      cancellationDate: invoice.cancellationDate,
    }));
  }

  buildSearch() {
    const search: Array<SFields | SConditionAND> = [];

    if (this.store.filters.dateFrom) {
      search.push({
        issueDate: {
          $gte: this.store.filters.dateFrom,
        },
      });
    }
    if (this.store.filters.dateTo) {
      search.push({
        issueDate: {
          $lte: this.store.filters.dateTo,
        },
      });
    }
    if (this.store.filters.docNumber) {
      search.push({
        $or: [
          {
            number: {
              $contL: this.store.filters.docNumber.trim(),
            },
          },
          {
            cancellationNumber: {
              $contL: this.store.filters.docNumber.trim(),
            },
          },
        ],
      });
    }
    if (this.store.filters.status) {
      search.push({
        status: {
          $eq: this.store.filters.status,
        },
      });
    } else {
      search.push({
        status: {
          $in: this.filterStatusOptions,
        },
      });
    }

    if (this.store.filters.search) {
      search.push({
        $or: [
          {
            number: {
              $contL: this.store.filters.search,
            },
          },
          {
            cancellationNumber: {
              $contL: this.store.filters.search,
            },
          },
          {
            'jobs.name': {
              $contL: this.store.filters.search,
            },
          },
          {
            outsideCommunicationNumber: {
              $contL: this.store.filters.search,
            },
          },
          {
            'target.name': {
              $contL: this.store.filters.search,
            },
          },
        ],
      });
    }

    return {
      search: {
        $and: search,
      },
    };
  }

  fetchAccountingDocs() {
    const { search } = this.buildSearch();

    // Reset to page 1 when performing a new search
    const query: CreateQueryParams = {
      page: 1,
      limit: this.store.pagination.perPage,
      sort: {
        field: this.store.pagination.sortBy,
        order: this.store.pagination.sortDir ? 'DESC' : 'ASC',
      },
    };

    // Update the store's current page to 1
    this.store.pagination.currentPage = 1;

    this.store[AccountingDocsActionsEnum.ITEMS_FETCH]({
      query,
      search,
    });
  }

  beforeDestroy() {
    // Clean up chat context
    this.clearChatContext('currentInvoices');
  }
}
